import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import { decodeJson, encodeJson, StringParam, withDefault } from 'use-query-params';

import ConversationsNoSearchResults from 'src/pages/landlord/conversations/components/list/ConversationsNoSearchResults';
import Header from 'src/pages/landlord/conversations/components/header/ConversationsHeader';
import NotificationAlert from 'src/pages/landlord/conversations/components/NotificationAlert';
import Page from 'src/components/page/PageV2';
import Responsive from 'src/components/responsive';
import Toolbar from 'src/pages/landlord/conversations/components/toolbar';
import type { IConversation } from 'src/api/landlord-messages-api';
import { Flex } from 'src/components/flex';
import { MobileConversationListItem } from 'src/pages/landlord/conversations/components/list/ConversationListItem';
import { SimpleDesktopConversationsTable } from 'src/pages/landlord/conversations/components/list/ConversationsListCategory';
import { getConversationsByCategory } from 'src/pages/landlord/conversations/utils/get-conversations-by-category';
import { type IConversationMatchWithChannel, conversationCategories, type EConversationCategory } from 'src/pages/landlord/conversations/context/types';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useConversationsSearch, isSearchEnabled, type IConversationMatch } from 'src/services/api/messages/use-conversations-search';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import { useProperties, type ELeadStage, type EMessageChannel } from 'src/services/api';
import { useTypedQueryParam } from 'src/hooks/use-typed-query-param';

const INITIAL_FILTERS: {
    assigneeIds: string[];
    ingestionSources: string[];
    leadStages: ELeadStage[];
    leadLabelIds: string[];
    excludeLeadLabelIds: string[];
    propertyIds: string[];
    hasNoProperty: boolean;
    propertyLabelIds: string[];
} = {
    assigneeIds: [],
    ingestionSources: [],
    leadStages: [],
    leadLabelIds: [],
    excludeLeadLabelIds: [],
    propertyIds: [],
    hasNoProperty: false,
    propertyLabelIds: [],
};

function NoSearchResultsYet() {
    const text = 'Enter a search query of at least 3 characters or select a filter to see results.';

    return <Box width="100%">
        <ConversationsNoSearchResults text={text} />
    </Box>;
}

type PageMainContentProps = {
    searchQuery?: string;
    searchFilter?: Record<string, any>;
    properties: any[];
    conversationsByCategory: Record<EConversationCategory, IConversation[]>;
    leadsByUuid: Map<string, any>;
    selectedConversations: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations: React.Dispatch<React.SetStateAction<Array<IConversation | IConversationMatchWithChannel>>>;
    searchConversationsForNavigation: (IConversation | IConversationMatchWithChannel)[];
};

/**
 * NOTE: This component should only be rendered if isLoading is false
 */
function PageMainContent({
    searchQuery,
    searchFilter,
    properties,
    conversationsByCategory,
    leadsByUuid,
    selectedConversations,
    setSelectedConversations,
    searchConversationsForNavigation
}: PageMainContentProps) {
    const { data: searchResults } = useConversationsSearch(
        searchQuery,
        searchFilter
    );

    const propertiesById = useMemo(() => {
        if (properties === undefined) {
            return undefined;
        }
        return new Map(properties.map((o) => [o.id, o]));
    }, [properties]);

    const conversations = useMemo(() => {
        if (!searchResults) {
            return [];
        }
        return searchResults.results.map((r) => {
            const o: IConversationMatch & {
                msg_channel?: EMessageChannel;
            } = Object.assign({}, r.match);
            o.msg_channel = r.msg_channel;
            return o as IConversationMatchWithChannel;
        });
    }, [searchResults]);

    return <Flex width="100%" justifyContent="center" flexWrap="nowrap">
        {
            !isSearchEnabled(searchQuery, searchFilter) &&
            <NoSearchResultsYet />
        }
        {
            (isSearchEnabled(searchQuery, searchFilter) && searchResults !== undefined && propertiesById !== undefined && searchResults.results.length > 0) &&
            <Paper>
                <Responsive desktop>
                    <SimpleDesktopConversationsTable
                        conversations={conversations}
                        conversationsByCategory={conversationsByCategory}
                        leadsByUuid={leadsByUuid}
                        selectedConversations={selectedConversations}
                        setSelectedConversations={setSelectedConversations}
                        searchConversationsForNavigation={searchConversationsForNavigation}
                    />
                </Responsive>
                <Responsive mobile>
                    <AccordionDetails sx={{ p: 0 }}>
                        {/* NOTE: the 100vw is necessary otherwise everything looks weird */}
                        <Box sx={{ overflowX: 'auto', maxWidth: '100vw' }}>
                            {searchResults.results.map((r) => {
                                const property = propertiesById.get(r.lead.property_id!);

                                const o: IConversationMatch & {
                                    msg_channel?: EMessageChannel;
                                } = Object.assign({}, r.match);
                                o.msg_channel = r.msg_channel;

                                return (
                                    <MobileConversationListItem
                                        key={r.lead.uuid}
                                        conversation={o as IConversationMatchWithChannel}
                                        property={property}
                                        conversationsByCategory={conversationsByCategory}
                                        leadsByUuid={leadsByUuid}
                                        setMobileMenuAnchorEl={() => { }}
                                        setSelectedConversations={setSelectedConversations}
                                        searchConversationsForNavigation={searchConversationsForNavigation}
                                    />
                                );
                            })}
                        </Box>
                    </AccordionDetails>
                </Responsive>
            </Paper>
        }
        {
            (isSearchEnabled(searchQuery, searchFilter) && searchResults !== undefined && propertiesById !== undefined && searchResults.results.length === 0) &&
            <Box width="100%">
                <ConversationsNoSearchResults text="No conversations match your search." />
            </Box>
        }
    </Flex >;
}

export default function ConversationsSearchPage() {
    // UI state
    const [selectedConversations, setSelectedConversations] = useState<(IConversation | IConversationMatchWithChannel)[]>([]);
    const [isFilterOpen, setFilterOpen] = useState(false);

    // Get server data from React Query and AuthenticatedState
    const { loggedInUser, leads, leadsByUuid } = useAuthenticatedState();
    const { data: properties } = useProperties();

    // URL query params for search and filter
    const [search, setSearch] = useTypedQueryParam<string>('s-search', withDefault(StringParam, ''));
    const [filter, _setFilter] = useTypedQueryParam<any>('s-filter', {
        encode: (value) => {
            if (value == null) {
                return value;
            }
            if (typeof value === 'object') {
                const withoutDefaults = Object.entries(value).reduce((acc, [key, val]) => {
                    if (key in INITIAL_FILTERS && isEqual(INITIAL_FILTERS[key as keyof typeof INITIAL_FILTERS], val)) {
                        return acc;
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    acc[key] = val;
                    return acc;
                }, {} as Partial<typeof INITIAL_FILTERS>);
                return encodeJson(withoutDefaults);
            }
            return encodeJson(value);
        },
        decode: (value) => {
            const decoded = decodeJson(value);
            if (typeof decoded === 'object' && decoded != null) {
                return {
                    ...INITIAL_FILTERS,
                    ...decoded,
                };
            }
            return INITIAL_FILTERS;
        },
    });

    const [persistentFilter, setPersistentFilter, , isPersistentFilterReady] = usePersistentState(
        'searchFilter',
        filter
    );

    const setFilter = useCallback((newFilter: Partial<typeof INITIAL_FILTERS>) => {
        const _newFilter = { ...filter, ...newFilter };
        _setFilter(_newFilter);
        setPersistentFilter(_newFilter);
    }, [filter, _setFilter, setPersistentFilter]);

    const resetFilter = useCallback(() => {
        _setFilter(INITIAL_FILTERS);
        setPersistentFilter(INITIAL_FILTERS);
    }, [_setFilter, setPersistentFilter]);

    // Apply persistent filter on mount
    useEffect(() => {
        if (!isPersistentFilterReady) {
            return;
        }
        if (isEqual(filter, INITIAL_FILTERS) && !isEqual(persistentFilter, INITIAL_FILTERS)) {
            // If no filter in URL but we have a persistent filter, apply it
            _setFilter(persistentFilter);
        }
    }, [filter, persistentFilter, isPersistentFilterReady, _setFilter]);

    // Reset selected conversations when search or filter changes
    useEffect(() => {
        setSelectedConversations([]);
    }, [search, filter]);

    // Get search results
    const { data: searchResults, isLoading: isSearchLoading } = useConversationsSearch(
        search,
        filter
    );

    // Convert search results to conversation format for navigation
    const searchConversationsForNavigation = useMemo(() => {
        if (!searchResults) {
            return [];
        }
        return searchResults.results.map((r) => {
            const o: IConversationMatch & {
                msg_channel?: EMessageChannel;
                latest_message_channel?: EMessageChannel;
            } = Object.assign({}, r.match);
            o.msg_channel = r.msg_channel;
            // Messages page expects latest_message_channel for navigation
            o.latest_message_channel = r.msg_channel;
            return o as IConversationMatchWithChannel;
        });
    }, [searchResults]);

    // CENTRALIZED: Organize search results by category (done once at top level)
    const conversationsByCategory = useMemo(() => {
        if (!searchResults || !leads) {
            return conversationCategories.reduce((acc, cat) => ({
                ...acc,
                [cat]: [],
            }), {} as Record<EConversationCategory, IConversation[]>);
        }

        const conversations = searchResults.results.map((r) => {
            const o: IConversationMatch & {
                msg_channel?: EMessageChannel;
            } = Object.assign({}, r.match);
            o.msg_channel = r.msg_channel;
            return o as any; // Will be categorized as IConversation
        });

        const conversationsWithArchiveStatus = conversations.map((conversation: any) => ({
            ...conversation,
            is_archived: leads.find((lead) => lead.uuid === conversation.lead_uuid)?.is_archived || false,
        }));

        return conversationCategories.reduce((acc, cat) => {
            return {
                ...acc,
                [cat]: getConversationsByCategory(conversationsWithArchiveStatus, cat)
            };
        }, {} as Record<EConversationCategory, IConversation[]>);
    }, [searchResults, leads]);


    // Loading state
    const { isLoading: isPropertiesLoading } = useProperties();
    const isLoading = !loggedInUser ||
        !leads ||
        !properties ||
        isSearchLoading ||
        isPropertiesLoading;

    return (
        <Page title="Reffie | Search Conversations" isLoading={isLoading}>
            {!isLoading && (
                <>
                    <Box flex={1}>
                        <NotificationAlert />
                        <Header
                            searchScope="all"
                            filter={filter}
                            setFilter={setFilter}
                            search={search}
                            setSearch={setSearch}
                            resetFilter={resetFilter}
                            isFilterOpen={isFilterOpen}
                            setFilterOpen={setFilterOpen}
                        />

                        {/* NOTE: no stages tab on this page */}

                        <PageMainContent
                            searchQuery={search}
                            searchFilter={filter}
                            properties={properties}
                            conversationsByCategory={conversationsByCategory}
                            leadsByUuid={leadsByUuid}
                            selectedConversations={selectedConversations}
                            setSelectedConversations={setSelectedConversations}
                            searchConversationsForNavigation={searchConversationsForNavigation}
                        />
                        <Toolbar
                            selectedConversations={selectedConversations}
                            setSelectedConversations={setSelectedConversations}
                        />

                        {/* NOTE: no hotkeys for search results for now */}
                    </Box>
                </>
            )}
        </Page>
    );
}
